import { DEFINITION_FILTER_KEYS, type AudienceDefinition } from './audience-definition';
import { accountCount, type TechnographicSelection } from '@/app/audience-builder/lib';
import { COMPANY_FILTER_CONFIGS, CONTACT_FILTER_CONFIGS } from './filter-configs';
import type { PlayKey } from '@/components/shared/profile';
import { normalizeSources, SIGNAL_SOURCE_OPTIONS } from '@/components/shared/signal-sources';
import { technologyPhrase } from '@/lib/technographics/condition';
import type { AccountDomains } from './overview-types';

/**
 * What Activate → carries into campaign targeting from a saved audience (NPJB-586): its
 * filters and its signal topics, and the play the
 * section was about. Since NPJB-763 the play is stored on the audience, with the accounts
 * it resolved to as a Company Domains filter.
 */

const PLAY_KEYS: ReadonlySet<string> = new Set<PlayKey>(['high_touch', 'nurture', 'awareness']);

/** The plays read types `play` as a string; only the three keys may be stored. */
export const isPlayKey = (value: string): value is PlayKey => PLAY_KEYS.has(value);

/**
 * The banner's Company Domains line. It names the cut when the list stopped at the cap, and
 * says so when the list could not be read — the field then waits for Refresh.
 */
export const domainsLine = (resolved: AccountDomains | null): string => {
  if (!resolved) return 'Company domains: not resolved. Use Refresh on the field.';
  const { domains, total, truncated } = resolved;
  if (truncated) {
    return `Company domains: first ${domains.length.toLocaleString()} of ${accountCount(total)}`;
  }
  return `Company domains: ${accountCount(domains.length)}`;
};

const LABEL_BY_KEY = new Map<string, string>(
  [...COMPANY_FILTER_CONFIGS, ...CONTACT_FILTER_CONFIGS].map(({ key, label }) => [key, label]),
);

/**
 * The banner's chips, one line per value: the play first, its
 * domains next (NPJB-763), then `Industry: Banking`, then `Signal topic: <name>`. A topic the index cannot name keeps
 * its id rather than disappearing. A `null` play is the whole audience (NPJB-832).
 */
export const carriedLines = (
  definition: AudienceDefinition,
  playLabel: string | null,
  topicName: (topicId: string) => string | undefined,
  domains?: string,
): string[] => {
  const filters = DEFINITION_FILTER_KEYS.flatMap((key) => {
    const label = LABEL_BY_KEY.get(key) ?? key;
    return definition.filters[key].map((value) => `${label}: ${value.label}`);
  });
  const topics = definition.signals.topics.map((id) => `Signal topic: ${topicName(id) ?? id}`);
  const playLine = playLabel === null ? 'Plays: All plays' : `Play: ${playLabel} Play`;
  const play = [playLine, ...(domains ? [domains] : [])];
  return [...play, ...filters, ...topics];
};

/**
 * NPJB-764 — the banner's lines for what now lands in the Signal Filters group: each
 * technology, then the sources when the audience was narrowed to some. All sources is the
 * default, so it gets no line.
 */
export const signalLines = (
  technographics: TechnographicSelection[],
  sources: readonly string[],
): string[] => {
  const tech = technographics.map((t) => `Technology: ${technologyPhrase(t.mode, t.label)}`);
  const on = normalizeSources(sources);
  if (on.length === 0) return tech;
  const names = SIGNAL_SOURCE_OPTIONS.filter((o) => on.includes(o.key)).map((o) => o.label);
  return [...tech, `Signal sources: ${names.join(', ')}`];
};
